import { useState, useEffect, useRef } from 'react';
import { watchResource, listResource } from '../services/k8s-proxy-client';

/**
 * Custom hook for watching Kubernetes metrics in real-time
 * 
 * @param metricType The type of metric to watch ('nodes' or 'pods')
 * @param namespace The namespace to filter by (only for pod metrics)
 * @returns Object containing metrics, loading state, and error
 */
export function useWatchMetrics(metricType: 'nodes' | 'pods', namespace = '') {
  const [metrics, setMetrics] = useState<any>({ items: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const resourceVersionRef = useRef('');
  const controllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    // Create a new abort controller
    const controller = new AbortController();
    controllerRef.current = controller;
    let retryTimeout: NodeJS.Timeout | undefined;

    async function startWatching() {
      try {
        setIsLoading(true);
        
        // Construct the API endpoint for metrics
        const endpoint = metricType === 'pods' && namespace
          ? `apis/metrics.k8s.io/v1beta1/namespaces/${namespace}/pods`
          : metricType === 'pods'
          ? `apis/metrics.k8s.io/v1beta1/pods`
          : `apis/metrics.k8s.io/v1beta1/nodes`;
        
        // Initial load of metrics
        const initialData = await fetch(`http://localhost:8181/${endpoint}`);
        if (!initialData.ok) {
          throw new Error(`Failed to fetch ${metricType} metrics: ${initialData.statusText}`);
        }
        
        const initialMetrics = await initialData.json();
        
        // Update resource version
        resourceVersionRef.current = initialMetrics.metadata?.resourceVersion || '';
        
        // Update state with initial metrics
        setMetrics(initialMetrics);
        setIsLoading(false);
        
        // Start watching for changes if we have a resource version
        if (resourceVersionRef.current) {
          watchMetricsWithRetry();
        }
      } catch (err) {
        console.error(`Error fetching ${metricType} metrics:`, err);
        setError(err instanceof Error ? err : new Error(`Unknown error occurred fetching ${metricType} metrics`));
        setIsLoading(false);
        
        // Retry after 5 seconds on error
        retryTimeout = setTimeout(startWatching, 5000);
      }
    }
    
    async function watchMetricsWithRetry() {
      try {
        // Construct the watch URL
        const watchEndpoint = metricType === 'pods' && namespace
          ? `apis/metrics.k8s.io/v1beta1/namespaces/${namespace}/pods?watch=1&resourceVersion=${resourceVersionRef.current}`
          : metricType === 'pods'
          ? `apis/metrics.k8s.io/v1beta1/pods?watch=1&resourceVersion=${resourceVersionRef.current}`
          : `apis/metrics.k8s.io/v1beta1/nodes?watch=1&resourceVersion=${resourceVersionRef.current}`;
        
        const response = await fetch(`http://localhost:8181/${watchEndpoint}`, { 
          signal: controller.signal 
        });
        
        if (!response.ok) {
          throw new Error(`Failed to watch ${metricType} metrics: ${response.statusText}`);
        }
        
        const reader = response.body!.getReader();
        const decoder = new TextDecoder('utf-8');
        let buffer = '';
        
        try {
          while (true) {
            const { done, value } = await reader.read();
            
            if (done) {
              break;
            }
            
            buffer += decoder.decode(value, { stream: true });
            
            const lines = buffer.split('\n');
            buffer = lines.pop() || ''; // Keep the last (potentially incomplete) line in the buffer
            
            for (const line of lines) {
              if (line.trim()) {
                try {
                  const event = JSON.parse(line);
                  
                  // Process the event based on type
                  if (event.type === 'ADDED' || event.type === 'MODIFIED') {
                    // Update the metrics based on the event
                    setMetrics(prevMetrics => {
                      // Create a new items array with the updated metric
                      const updatedItems = [...prevMetrics.items];
                      const index = updatedItems.findIndex(
                        item => item.metadata.name === event.object.metadata.name
                      );
                      
                      if (index >= 0) {
                        updatedItems[index] = event.object;
                      } else {
                        updatedItems.push(event.object);
                      }
                      
                      return {
                        ...prevMetrics,
                        items: updatedItems
                      };
                    });
                  } else if (event.type === 'DELETED') {
                    // Remove the deleted metric
                    setMetrics(prevMetrics => {
                      const updatedItems = prevMetrics.items.filter(
                        item => item.metadata.name !== event.object.metadata.name
                      );
                      
                      return {
                        ...prevMetrics,
                        items: updatedItems
                      };
                    });
                  }
                  
                  // Update the resource version for the next watch
                  if (event.object?.metadata?.resourceVersion) {
                    resourceVersionRef.current = event.object.metadata.resourceVersion;
                  }
                } catch (error) {
                  console.error('Error parsing metrics event:', error, line);
                }
              }
            }
          }
        } finally {
          reader.releaseLock();
        }
        
        // If we reach here, the watch ended, restart it
        watchMetricsWithRetry();
      } catch (err) {
        if (controller.signal.aborted) {
          // This was an intentional abort, do nothing
          return;
        }
        
        console.error(`Error watching ${metricType} metrics:`, err);
        // Retry after 5 seconds on error
        retryTimeout = setTimeout(watchMetricsWithRetry, 5000);
      }
    }
    
    // Start the initial fetch
    startWatching();
    
    // Cleanup function
    return () => {
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
      controller.abort();
    };
  }, [metricType, namespace]);
  
  return { metrics, isLoading, error };
} 